import { JSX } from 'preact';
import { config } from '@/config';

// Local SVG imports for known platforms
import iconInstagram from '@/ui/icons/instagram.svg';
import iconLinkedin from '@/ui/icons/linkedin.svg';
import iconFacebook from '@/ui/icons/facebook.svg';
import iconX from '@/ui/icons/x.svg';
import iconTelegram from '@/ui/icons/telegram.svg';
import iconYoutube from '@/ui/icons/youtube.svg';
import iconWhatsapp from '@/ui/icons/whatsapp.svg';
import iconTiktok from '@/ui/icons/tiktok.svg';
import iconThreads from '@/ui/icons/threads.svg';
import iconLikee from '@/ui/icons/likee.svg';
import iconDiscord from '@/ui/icons/discord.svg';
import iconClubhouse from '@/ui/icons/clubhouse.svg';

type PlatName = 'telegram' | 'instagram' | 'x' | 'linkedin' | 'discord' | 'facebook' | string;

const localIcons: Record<string, string> = {
  instagram: iconInstagram,
  linkedin: iconLinkedin,
  facebook: iconFacebook,
  x: iconX,
  telegram: iconTelegram,
  youtube: iconYoutube,
  whatsapp: iconWhatsapp,
  tiktok: iconTiktok,
  threads: iconThreads,
  likee: iconLikee,
  discord: iconDiscord,
  clubhouse: iconClubhouse,
};

const colors: Record<string, { bg: string; fg: string }> = {
  telegram:  { bg: '#229ED9', fg: '#fff' },
  instagram: { bg: 'linear-gradient(135deg, #f58529, #dd2a7b, #8134af)', fg: '#fff' },
  x:         { bg: '#000',    fg: '#fff' },
  linkedin:  { bg: '#0A66C2', fg: '#fff' },
  discord:   { bg: '#5865F2', fg: '#fff' },
  facebook:  { bg: '#1877F2', fg: '#fff' },
};

const marks: Record<string, (s: number) => JSX.Element> = {
  telegram: (s) => (
    <svg width={s * 0.6} height={s * 0.6} viewBox="0 0 24 24" fill="#fff">
      <path d="M22 3 2 11l6 2 2 7 4-4 6 5 2-18zM10 15v-2l9-7-6 9z"/>
    </svg>
  ),
  instagram: (s) => (
    <svg width={s * 0.6} height={s * 0.6} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="5"/>
      <circle cx="12" cy="12" r="4"/>
      <circle cx="17.5" cy="6.5" r="0.8" fill="#fff" stroke="none"/>
    </svg>
  ),
  x: (s) => (
    <svg width={s * 0.5} height={s * 0.5} viewBox="0 0 24 24" fill="#fff">
      <path d="M4 4l7 8.5L4 20h2l6-6.8L17 20h3l-7.5-9L19 4h-2l-5.5 6.2L7 4z"/>
    </svg>
  ),
  linkedin: (s) => (
    <svg width={s * 0.55} height={s * 0.55} viewBox="0 0 24 24" fill="#fff">
      <path d="M4 4h4v16H4zM6 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4zM10 8h4v2c.6-1 2-2 4-2 3 0 4 2 4 5v7h-4v-6c0-1.5-.5-3-2-3s-2 1.5-2 3v6h-4z"/>
    </svg>
  ),
  discord: (s) => (
    <svg width={s * 0.6} height={s * 0.6} viewBox="0 0 24 24" fill="#fff">
      <path d="M19 4.5A18 18 0 0 0 15 3l-.3.6a14 14 0 0 0-5.4 0L9 3a18 18 0 0 0-4 1.5C2 9.5 1 14 1.5 18a18 18 0 0 0 5.5 3l1.2-1.7a11 11 0 0 1-2-1l.5-.4a13 13 0 0 0 10.6 0l.5.4a11 11 0 0 1-2 1L16.5 21a18 18 0 0 0 5.5-3c.6-5-.6-9-3-13.5zM9 15a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm6 0a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"/>
    </svg>
  ),
  facebook: (s) => (
    <svg width={s * 0.55} height={s * 0.55} viewBox="0 0 24 24" fill="#fff">
      <path d="M22 12a10 10 0 1 0-11.6 9.9V15h-2.5v-3h2.5V9.8c0-2.5 1.5-3.8 3.7-3.8 1.1 0 2.2.2 2.2.2v2.4h-1.3c-1.2 0-1.6.8-1.6 1.6V12H16l-.4 3h-2.3v6.9A10 10 0 0 0 22 12z"/>
    </svg>
  ),
};

export const Plat = ({ name, size = 28, logourl }: {
  name: PlatName; size?: number; logourl?: string | null;
}) => {
  const key = name.trim().toLowerCase();
  if (key === 'special') {
    return (
      <div style={{
        width: size, height: size, borderRadius: size * 0.32,
        background: 'linear-gradient(135deg, #c6ff3d, #48e0c2)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: '#0a0c0f', flexShrink: 0,
      }}>
        <span aria-hidden="true" style={{ fontSize: size * 0.58, fontWeight: 900, lineHeight: 1 }}>★</span>
      </div>
    );
  }

  const c = colors[key] ?? { bg: '#333', fg: '#fff' };
  const localIcon = localIcons[key];
  const remoteIcon = logourl || undefined;

  // Known platform with inline SVG mark — always show colored bg + white mark
  const mark = marks[key];
  if (mark) {
    return (
      <div style={{
        width: size, height: size, borderRadius: size * 0.32, background: c.bg,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: c.fg, flexShrink: 0,
      }}>
        {mark(size)}
      </div>
    );
  }

  // Known platform with local SVG file — show as img
  const preferLocal = config.iconPriority;
  const imgSrc = preferLocal ? (localIcon || remoteIcon) : (remoteIcon || localIcon);

  if (imgSrc) {
    const onImgError = (e: JSX.TargetedEvent<HTMLImageElement, Event>) => {
      if (!localIcon || e.currentTarget.src.endsWith(localIcon)) return;
      e.currentTarget.src = localIcon;
    };

    return (
      <div style={{
        width: size, height: size, borderRadius: size * 0.32,
        background: c.bg, display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', flexShrink: 0,
      }}>
        <img src={imgSrc} alt={name} width={size} height={size}
          onError={onImgError} style={{ objectFit: 'contain' }}/>
      </div>
    );
  }

  // Unknown platform — first letter fallback
  return (
    <div style={{
      width: size, height: size, borderRadius: size * 0.32, background: c.bg,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      color: c.fg, flexShrink: 0,
    }}>
      <span style={{ fontWeight: 700, fontSize: size * 0.42 }}>{name[0].toUpperCase()}</span>
    </div>
  );
};
